import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ─── SVG Bar Chart ────────────────────────────────────────────────────────────
function BarChart({ data }) {
  if (!data || data.length === 0) return <div className="text-center text-muted py-4 small">No data yet</div>;
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const W = 420, H = 160, padL = 28, padB = 28, padT = 18, padR = 8;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const gap = chartW / data.length;
  const barW = gap * 0.52;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#c7d2fe" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map(frac => {
        const y = padT + chartH * (1 - frac);
        return (
          <g key={frac}>
            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
            <text x={padL - 4} y={y + 3.5} fontSize={8} fill="#94a3b8" textAnchor="end">
              {Math.round(maxVal * frac)}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const barH = Math.max((d.count / maxVal) * chartH, d.count > 0 ? 3 : 0);
        const x = padL + i * gap + gap / 2 - barW / 2;
        const y = padT + chartH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} rx={3} fill="url(#barGrad)" />
            {d.count > 0 && (
              <text x={x + barW / 2} y={y - 4} fontSize={8} fill="#6366f1" textAnchor="middle" fontWeight="700">
                {d.count}
              </text>
            )}
            <text x={x + barW / 2} y={H - 7} fontSize={8.5} fill="#64748b" textAnchor="middle">
              {d.month}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── SVG Donut Chart ──────────────────────────────────────────────────────────
const DONUT_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

function DonutChart({ data }) {
  if (!data || data.length === 0) return <div className="text-center text-muted py-4 small">No data yet</div>;
  const total = data.reduce((s, d) => s + d.count, 0);
  const cx = 75, cy = 75, r = 58, innerR = 36;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const sweep = (d.count / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
    const x2 = cx + r * Math.cos(angle + sweep), y2 = cy + r * Math.sin(angle + sweep);
    const ix1 = cx + innerR * Math.cos(angle), iy1 = cy + innerR * Math.sin(angle);
    const ix2 = cx + innerR * Math.cos(angle + sweep), iy2 = cy + innerR * Math.sin(angle + sweep);
    const large = sweep > Math.PI ? 1 : 0;
    const path = `M${ix1},${iy1} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${ix2},${iy2} A${innerR},${innerR} 0 ${large} 0 ${ix1},${iy1}Z`;
    const s = { path, color: DONUT_COLORS[i % DONUT_COLORS.length], name: d.name || d._id, count: d.count };
    angle += sweep;
    return s;
  });
  return (
    <div className="d-flex align-items-center gap-3">
      <svg viewBox="0 0 150 150" width={130} height={130} style={{ flexShrink: 0 }}>
        {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} />)}
        <text x={cx} y={cy - 7} textAnchor="middle" fontSize={20} fontWeight="800" fill="#0f172a">{total}</text>
        <text x={cx} y={cy + 9} textAnchor="middle" fontSize={9} fill="#64748b">Total</text>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {slices.map((s, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: 12 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <div className="text-truncate text-capitalize" style={{ flex: 1, color: '#374151' }}>{s.name || '—'}</div>
            <div style={{ fontWeight: 700, color: '#0f172a', minWidth: 20, textAlign: 'right' }}>{s.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Industry Bars ────────────────────────────────────────────────────────────
function IndustryBars({ data }) {
  if (!data || data.length === 0) return <div className="text-center text-muted py-4 small">No data yet</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  const COLORS_IND = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 12 }}>
            <span style={{ color: '#374151', fontWeight: 500 }}>
              <span className="me-1">{d.icon || '🏢'}</span>{d._id}
            </span>
            <span style={{ fontWeight: 700, color: '#0f172a' }}>{d.count}</span>
          </div>
          <div style={{ height: 6, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(d.count / max) * 100}%`, background: DONUT_COLORS[i % DONUT_COLORS.length], borderRadius: 8, transition: 'width 0.6s ease' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ title, value, icon, color, sub, trend }) {
  return (
    <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: 14 }}>
      <div className="card-body" style={{ padding: '18px 20px' }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: 4 }}>{value ?? '—'}</div>
          </div>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={`bi ${icon}`} style={{ fontSize: 20, color }} />
          </div>
        </div>
        {(sub || trend !== undefined) && (
          <div className="d-flex align-items-center gap-2 mt-1" style={{ fontSize: 12 }}>
            {trend !== undefined && (
              <span style={{ color: trend >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                <i className={`bi bi-arrow-${trend >= 0 ? 'up' : 'down'}-short`} />{Math.abs(trend)}%
              </span>
            )}
            {sub && <span style={{ color: '#94a3b8' }}>{sub}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Severity badge ───────────────────────────────────────────────────────────
const SEV_MAP = { info: { bg: '#dbeafe', color: '#1d4ed8', icon: 'bi-info-circle' }, success: { bg: '#dcfce7', color: '#166534', icon: 'bi-check-circle' }, warning: { bg: '#fef9c3', color: '#854d0e', icon: 'bi-exclamation-triangle' }, error: { bg: '#fee2e2', color: '#991b1b', icon: 'bi-x-octagon' } };

function SevBadge({ sev }) {
  const s = SEV_MAP[sev] || SEV_MAP.info;
  return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}><i className={`bi ${s.icon} me-1`} />{sev}</span>;
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function SADashboard() {
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [sRes, nRes] = await Promise.all([
        api.get('/sa/stats'),
        api.get('/sa/notifications?limit=8'),
      ]);
      setStats(sRes.data.data);
      setNotifications(nRes.data.data?.notifications || nRes.data.data || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.patch(`/sa/notifications/${id}/read`);
    setNotifications(n => n.map(x => x._id === id ? { ...x, isRead: true } : x));
  };

  const markAllRead = async () => {
    await api.patch('/sa/notifications/read-all');
    setNotifications(n => n.map(x => ({ ...x, isRead: true })));
  };

  if (loading) return <LoadingSpinner fullPage text="Loading platform stats…" />;

  const s = stats || {};
  const unread = notifications.filter(n => !n.isRead).length;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
            <i className="bi bi-speedometer2 me-2 text-primary" />Platform Dashboard
          </h4>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Real-time overview of all tenants and platform activity</div>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={load} style={{ borderRadius: 8 }}>
          <i className="bi bi-arrow-clockwise me-1" />Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Total Tenants" value={s.totalTenants} icon="bi-buildings" color="#6366f1" sub="all time" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Active" value={s.activeTenants} icon="bi-check-circle-fill" color="#10b981" sub="tenants" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Suspended" value={s.suspendedTenants ?? 0} icon="bi-pause-circle-fill" color="#ef4444" sub="tenants" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Total Users" value={s.totalUsers} icon="bi-people-fill" color="#06b6d4" sub="across all tenants" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Est. MRR" value={s.mrr ? `₹${(s.mrr / 100000).toFixed(1)}L` : '—'} icon="bi-currency-rupee" color="#f59e0b" sub="monthly revenue" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <KpiCard title="Notifications" value={unread} icon="bi-bell-fill" color="#8b5cf6" sub="unread alerts" />
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-3 mb-3">
        {/* Monthly Growth Bar Chart */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body" style={{ padding: '20px 20px 12px' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Monthly Tenant Growth</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>New tenants added per month</div>
                </div>
                <span className="badge" style={{ background: '#ede9fe', color: '#6366f1', borderRadius: 8, fontWeight: 600, fontSize: 12 }}>
                  Last 6 Months
                </span>
              </div>
              <BarChart data={s.monthlyGrowth || []} />
            </div>
          </div>
        </div>

        {/* Plan Donut */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body" style={{ padding: '20px 20px 14px' }}>
              <div className="mb-3">
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Tenant by Plan</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Distribution across subscription tiers</div>
              </div>
              <DonutChart data={s.planBreakdown || []} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="row g-3">
        {/* Industry Breakdown */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body" style={{ padding: '20px 20px 16px' }}>
              <div className="mb-3">
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Industry Breakdown</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Tenants per industry vertical</div>
              </div>
              <IndustryBars data={s.industryBreakdown || []} />
            </div>
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body p-0">
              <div style={{ padding: '18px 20px 12px' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Recent Tenants</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Latest onboarded organizations</div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr>
                      <th style={{ padding: '8px 16px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: 10.5, letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: 10.5, letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>Plan</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: 10.5, letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.recentTenants || []).map(t => (
                      <tr key={t._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '9px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 12 }}>{t.name}</div>
                          <div style={{ color: '#94a3b8', fontSize: 10.5 }}>{t.industryId?.name}</div>
                        </td>
                        <td style={{ padding: '9px 12px' }}>
                          <span style={{ background: '#ede9fe', color: '#6366f1', padding: '2px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 600, textTransform: 'capitalize' }}>
                            {t.planId?.tier || t.planId?.name || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '9px 12px' }}>
                          <span style={{ background: t.isActive ? '#dcfce7' : '#fee2e2', color: t.isActive ? '#166534' : '#991b1b', padding: '2px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 600 }}>
                            {t.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {(s.recentTenants || []).length === 0 && (
                      <tr><td colSpan={3} className="text-center text-muted py-3" style={{ fontSize: 12 }}>No tenants yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body p-0">
              <div className="d-flex align-items-center justify-content-between" style={{ padding: '18px 20px 12px' }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Activity Feed</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Recent platform events</div>
                </div>
                {unread > 0 && (
                  <button className="btn btn-sm" onClick={markAllRead}
                    style={{ fontSize: 11, padding: '2px 10px', background: '#ede9fe', color: '#6366f1', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                    Mark all read
                  </button>
                )}
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {notifications.length === 0 && (
                  <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                    <i className="bi bi-bell-slash d-block mb-1" style={{ fontSize: 22 }} />No notifications yet
                  </div>
                )}
                {notifications.map(n => (
                  <div key={n._id}
                    onClick={() => !n.isRead && markRead(n._id)}
                    style={{ padding: '10px 18px', borderBottom: '1px solid #f1f5f9', cursor: n.isRead ? 'default' : 'pointer', background: n.isRead ? 'transparent' : '#faf9ff', transition: 'background 0.2s' }}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: n.isRead ? 500 : 700, color: '#0f172a', fontSize: 12, lineHeight: 1.3 }}>{n.title}</div>
                        {n.message && <div style={{ color: '#64748b', fontSize: 11, marginTop: 2, lineHeight: 1.35 }}>{n.message}</div>}
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <SevBadge sev={n.severity} />
                          <span style={{ fontSize: 10.5, color: '#94a3b8' }}>{timeAgo(n.createdAt)}</span>
                        </div>
                      </div>
                      {!n.isRead && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#6366f1', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                  </div>
                ))}
              </div>
              {notifications.length > 0 && (
                <div style={{ padding: '10px 18px', borderTop: '1px solid #f1f5f9' }}>
                  <a href="/sa/notifications" style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>
                    View all notifications <i className="bi bi-arrow-right" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
